import { useState } from "react";
import { FiX } from "react-icons/fi";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { HelpIcon } from "newui/components/ui/Help";
import { cn } from "utils/helpers";
import {
	scopeKey,
	scopeTypeStyle,
	useCompanyScopeTypes,
	useOfferableScopeTypes,
} from "newui/features/RolesPermissions";
import type {
	ScopeExclusion,
	ScopeType,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { useCurrentCompanyValue } from "hooks/auth";
import { BIDDER_ROLE_RFX_ONLY_COPY, CASCADE_COPY, RFX_ONLY_COPY } from "./AccessInfoNotice";
import { ScopePickerList } from "./ScopePickerList";
import { DowngradeConfirmModal } from "./DowngradeConfirmModal";
import type { UseScopeSelectionResult } from "./useScopeSelection";

export interface ScopePickerProps {
	/** Single-target mode (CONTRACT §3.5) only when this is exactly 1 user. */
	targetUserIds: number[];
	roleId: number | null;
	/**
	 * `roles.side` of the SELECTED role (`procurer | bidder | both`), as the API
	 * serves it on every `RoleResource`. A bidder role narrows the picker to the
	 * RFx list even on a hybrid company that owns portfolios and projects — see
	 * `useOfferableScopeTypes`. Null/absent narrows nothing.
	 */
	roleSide: string | null;
	selection: UseScopeSelectionResult;
	/**
	 * Author retest — "background stays very dark after finishing joyride".
	 * The cascade-help tour used to be owned here (`useGuidedTour` called
	 * directly inside `ScopePicker`), the same shape the crash fix already
	 * moved `modeHelp` AWAY from for `AccessModeSelector`. Turns out that
	 * shape was wrong for the SAME reason here: `AddAccessFlow`'s Modal keeps
	 * a closing dialog's children mounted, UNCHANGED, through its own exit
	 * transition (Chakra/framer-motion freezes the last-rendered subtree
	 * rather than re-reading new props from a re-render its own `isOpen`
	 * already turned off) — so by the time `ScopePicker` naturally unmounts
	 * (if it ever cleanly does, mid-animation, at all), any tour it owned
	 * itself had already been left running, invisibly, under whatever opened
	 * next. `AddAccessFlow` itself is NOT inside that frozen subtree — it is
	 * Modal's own caller — so it owns this tour now instead, the same place
	 * `modeHelp` already lives, stopped via the exact same `step`-driven
	 * effect. `onCascadeHelpStart` is what is left for `ScopePicker` to do:
	 * render the trigger in the right place in ITS OWN layout (above the
	 * search box, per author item 4.1/PRO-757's cascade-icon repositioning)
	 * without owning the tour's lifecycle. The tour's OWN portal is rendered
	 * directly by `AddAccessFlow`, NOT passed down as a prop and rendered
	 * here — a `TourPortal` value computed on one of `AddAccessFlow`'s
	 * renders while `ScopePicker` itself is frozen (mid-exit) would never
	 * reach this component either, the exact problem this fix exists to
	 * avoid. A `createPortal(..., document.body)` value doesn't care where
	 * in the React tree it's rendered from for DOM placement, only that the
	 * component holding it keeps re-rendering — so it stays with the
	 * component that's guaranteed to.
	 */
	onCascadeHelpStart: () => void;
}

/**
 * Every list the picker can render, in cascade order (broadest first). Which of
 * them this company actually gets is decided by `useOfferableScopeTypes()` — a
 * bidder-only company has no portfolios or projects (PRO-757 follow-up). This
 * FULL order is still what `ancestorExclude()` reasons about, because "which
 * types are above me in the cascade" is a property of the cascade, not of which
 * lists happen to be on screen.
 */
const LIST_TYPES: ScopeType[] = ["project_type", "project", "rfx"];

// Search-results list section headers (D8-T2) — distinct from the "ASSIGNED ACCESS LEVELS"
// summary titles below (author item 4.3), which use the ticket's own screenshot wording.
// `project_type`'s entry is the STATIC default; author item 4.5 (G6) overrides it dynamically
// per company below — see `listLabel`.
const DEFAULT_LIST_LABEL: Record<ScopeType, string> = {
	global: "Company Wide",
	project_type: "Project Types",
	project: "Projects",
	rfx: "RFx Events",
};

// Author item 4.3 — summary section titles, verbatim from specific-access.png. `global` is
// unused here (§3.1 — the picker never renders a `global` list).
const SUMMARY_SECTION_LABEL: Record<ScopeType, string> = {
	global: "Company Wide",
	project_type: "PORTFOLIO WIDE ACCESS",
	project: "PROJECT WIDE ACCESS",
	rfx: "SPECIFIC RFX ACCESS",
};

// D8-T2 — three independently-paginated, exclusion-aware lists sharing one
// search box and one cross-type selection (CONTRACT §3). The `global` option
// is NOT part of this component (§3.1) — it's the mode toggle in
// AccessModeSelector.
export const ScopePicker = ({
	targetUserIds,
	roleId,
	roleSide,
	selection,
	onCascadeHelpStart,
}: ScopePickerProps) => {
	const [query, setQuery] = useState("");
	const {
		selectedScopes,
		exclude,
		isSelected,
		toggle,
		remove,
		registerAlreadyGranted,
		pendingDowngrade,
		confirmDowngrade,
		cancelDowngrade,
	} = selection;

	/**
	 * Author item 4.5 / ticket lines 49-50 (G6) — CORRECTED per the coordinator's 2026-09-24
	 * course-correction. CONTRACT §2 collapsed two separate ticket requirements into one ("always
	 * show `project_type_label` as the header"); the ticket's own lines 49-50 actually ask for TWO
	 * different behaviours depending on whether the company's portfolios are grouped by project
	 * type:
	 *
	 *   - >1 project type (`has_project_type_grouping: true`) -> the SECTION HEADER becomes the
	 *     company's own `project_type_label` (e.g. "Portfolio Type"); the ROWS are unchanged (the
	 *     real portfolio names).
	 *   - exactly 1 project type (`has_project_type_grouping: false`) -> the header stays the
	 *     STATIC "Project Types" (unchanged); the single ROW is relabelled `All Projects` instead
	 *     of showing its real (singular, uninformative) name.
	 *
	 * Defaults to the grouped/no-override branch while the company hasn't loaded yet (`null`) —
	 * defaulting the other way would relabel every row "All Projects" for every company on first
	 * paint, which is wrong far more often than it is right.
	 *
	 * No RFx/Projects-equivalent field exists on `Company`/`UserCompanyProfile` (grepped both
	 * types — the only fields are `project_type_label`/`has_project_type_grouping`), so those two
	 * headers stay static; only Portfolio's is ever dynamic.
	 */
	/**
	 * PRO-757 follow-up — which lists this company gets at all. A bidder-only
	 * company owns no portfolios and no projects, so those two lists could only
	 * ever say "No matches."; it gets the RFx list alone (the RFx it has been
	 * invited to — the catalogue already admits those via `rfx_invites`).
	 * Server-decided; see the hook for why it is not derived here.
	 */
	const visibleListTypes = useOfferableScopeTypes(roleSide);
	const rfxOnly = visibleListTypes.length === 1 && visibleListTypes[0] === "rfx";

	/**
	 * WHY the picker is down to the RFx list decides which sentence explains it.
	 * `useCompanyScopeTypes()` is the company's answer BEFORE the role narrowing,
	 * so a hybrid company that lost its portfolio/project lists purely because a
	 * bidder role is selected is told that, rather than being told its company is
	 * only ever invited to events — which for a hybrid is simply untrue.
	 */
	const companyScopeTypes = useCompanyScopeTypes();
	const companyIsRfxOnly = companyScopeTypes.length === 1 && companyScopeTypes[0] === "rfx";

	const hasProjectTypeGroupingRaw = useCurrentCompanyValue("has_project_type_grouping");
	const projectTypeLabelRaw = useCurrentCompanyValue("project_type_label");
	const hasProjectTypeGrouping = hasProjectTypeGroupingRaw ?? true;
	const projectTypeListLabel =
		hasProjectTypeGrouping && projectTypeLabelRaw
			? projectTypeLabelRaw
			: DEFAULT_LIST_LABEL.project_type;
	const projectTypeRowOverride = hasProjectTypeGrouping ? undefined : "All Projects";

	const listLabel: Record<ScopeType, string> = {
		...DEFAULT_LIST_LABEL,
		project_type: projectTypeListLabel,
	};

	// §3.5 — user_id/role_id sent ONLY when the flow targets exactly one user.
	const singleTargetUserId = targetUserIds.length === 1 ? targetUserIds[0] : undefined;
	const singleTargetRoleId =
		singleTargetUserId !== undefined && roleId !== null ? roleId : undefined;

	const selectedByType = LIST_TYPES.reduce<Record<ScopeType, typeof selectedScopes>>(
		(groups, type) => ({ ...groups, [type]: selectedScopes.filter((s) => s.scope_type === type) }),
		{ project_type: [], project: [], rfx: [], global: [] }
	);

	/**
	 * Author retest 2.2/2.4 — exclusion only ever cascades DOWNWARD
	 * (project_type -> project -> rfx, `LIST_TYPES`' own order). A list of
	 * type T only needs to reset/refetch when exclusions of types STRICTLY
	 * ABOVE T change — same-type and below-type exclusions can never cover a
	 * row in T's own list. Passing the SAME full cross-type `exclude[]` to
	 * every list (as before) meant picking anything of any type reset AND
	 * refetched all three: RFx (bottom of the chain) should never reset
	 * ANYTHING, and a Project pick should only affect the RFx list, not
	 * itself or Portfolio.
	 *
	 * Same-type entries are deliberately dropped here, not just left alone —
	 * the server's own self-exclusion guard already keeps a selected node
	 * visible in its own list regardless (preserved when the backend
	 * engineer fixed F3), so sending them back achieves nothing except
	 * needlessly changing this list's `useScopeOptions` query key (which
	 * react-query hashes by content, not reference — so this alone would
	 * trigger a real network refetch even if `ScopePickerList`'s own local
	 * pagination-reset logic were untouched).
	 */
	const ancestorExclude = (type: ScopeType): ScopeExclusion[] => {
		const ancestorTypes = LIST_TYPES.slice(0, LIST_TYPES.indexOf(type));
		return exclude.filter((entry) => ancestorTypes.includes(entry.scope_type));
	};

	return (
		<div>
			{/* Author item 3.3 — the cascade sentence (A6/§10.3 frozen copy) leads the picker step
			    under the author's recorded amendment: it used to sit on the mode step, "the options
			    below" it, but the picker itself moved to a later step, so it now lives where the
			    options actually are. See AccessInfoNotice.tsx's own comment for the full amendment
			    record and CASCADE_COPY's export. */}
			<div
				className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-sm leading-relaxed text-blue-900"
				data-testid="scope-cascade-notice"
				role="note"
			>
				{(() => {
					if (!rfxOnly) {
						return CASCADE_COPY;
					}

					return companyIsRfxOnly ? RFX_ONLY_COPY : BIDDER_ROLE_RFX_ONLY_COPY;
				})()}
			</div>

			{/* Author item 4.3 — "ASSIGNED ACCESS LEVELS" moves to the TOP of this step, above the
			    search box, restructured into the ticket's own three groups (screenshot wording
			    verbatim) rather than the generic "Selected scope" summary this replaces. */}
			<div className="mb-4" data-testid="scope-chip-container">
				<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
					Assigned Access Levels
				</p>
				{selectedScopes.length === 0 ? (
					<div className="flex min-h-[56px] items-center justify-center rounded-lg border border-dashed border-gray-200 text-xs text-gray-500">
						No scope selected yet — search below and click a result to add it.
					</div>
				) : (
					LIST_TYPES.map((type) => {
						const options = selectedByType[type];
						if (options.length === 0) {
							return null;
						}
						// Author item 4.4 — each summary section is tinted in its own type's colour
						// family, matching the chips inside it and the selected rows below.
						const style = scopeTypeStyle(type);
						return (
							<div key={type} className="mb-3">
								<h4
									className={cn(
										"mb-2 text-[11.5px] font-semibold uppercase tracking-wide",
										style.rowSelectedText
									)}
									data-testid={`assigned-access-section-${type}`}
								>
									{SUMMARY_SECTION_LABEL[type]} ({options.length})
								</h4>
								<div className="flex flex-wrap gap-2 rounded-lg border border-dashed border-gray-200 p-3.5">
									{options.map((option) => {
										const key = scopeKey(option.scope_type, option.scope_id);
										return (
											<span
												key={key}
												className={cn(
													"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs",
													style.chip
												)}
											>
												{option.label}
												<button
													type="button"
													onClick={() => remove(option)}
													aria-label={`Remove ${option.label}`}
													data-testid={`scope-chip-remove-${key}`}
													className="opacity-65 hover:opacity-100"
												>
													<FiX size={12} />
												</button>
											</span>
										);
									})}
								</div>
							</div>
						);
					})
				)}
			</div>

			{/* PRO-757 — was floating to the right of the search box (bottom-aligned via
			    `items-end` against the label+input stack), reading as detached from the
			    control it explains and overlapping the results list below at narrower
			    widths. Moved to its own line directly above the search box — the same
			    "attached to what it explains" positioning the mode step's own trigger
			    uses (author item 4.1's "Need some help " label, unchanged). */}
			<div className="mb-1 flex justify-end">
				<HelpIcon
					parent={<span className="text-sm text-gray-600">Need some help </span>}
					onClick={onCascadeHelpStart}
					label="I cannot find something - help"
					data-testid="cascade-help-tooltip"
				/>
			</div>
			<InputWithLabel
				// PRO-757 follow-up — naming lists that aren't on screen is worse than
				// saying less: a bidder-only admin sees one list and was told to search
				// three.
				label={
					rfxOnly ? "Search RFx events" : "Search project types, projects, RFx events"
				}
				testId="scope-search-input"
				placeholder={
					rfxOnly
						? "Type to filter e.g. 'RFT-2026', 'earthworks'…"
						: "Type to filter e.g. 'motorway', 'water', 'RFT-2026'…"
				}
				value={query}
				onChange={(event) => setQuery(event.target.value)}
				// PRO-757 — measured live (Playwright, not jsdom): this input's own
				// wrapper is `position: relative` by default (needed only for the
				// icon/toggler features this instance doesn't use), which becomes
				// react-joyride's `offsetParent` for the cascade-help tour's first
				// step — a CLOSER positioned ancestor than Modal.tsx's own fixed
				// dialog container, reintroducing the same offsetTop bug
				// `neutralizeInternalPositioning` already fixed at the modal level.
				// See `wrapperPosition`'s own doc comment on TextInputWithLabel.
				wrapperPosition="static"
			/>

			<div className="mt-2 space-y-3">
				{visibleListTypes.map((type) => (
					<ScopePickerList
						key={type}
						type={type}
						typeLabel={listLabel[type]}
						query={query}
						exclude={ancestorExclude(type)}
						userId={singleTargetUserId}
						roleId={singleTargetRoleId}
						isSelected={isSelected}
						onToggle={toggle}
						onAlreadyGrantedChange={registerAlreadyGranted}
						rowLabelOverride={type === "project_type" ? projectTypeRowOverride : undefined}
					/>
				))}
			</div>

			<DowngradeConfirmModal
				pendingDowngrade={pendingDowngrade}
				onConfirm={confirmDowngrade}
				onCancel={cancelDowngrade}
			/>
		</div>
	);
};
